/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, merge } from 'rxjs';
import { map } from 'rxjs/operators';

import { CustomPropertyInterface } from '@reach/interfaces/custom-property/custom-property.interface';

import { type NodeSettingItem, NodeSettingsApiService } from '~app-client/api/services';

/** Mark some properties which only the former including as optional and set the value to never */
type Without<T, U> = Partial<Record<Exclude<keyof T, keyof U>, never>>;

/** get the XOR type which could make 2 types exclude each other */
export type XOR<T, U> = T | U extends object ? (Without<T, U> & U) | (Without<U, T> & T) : T | U;

export type AdditionalSettingsOptions = string;

@Injectable({ providedIn: 'root' })
export class NodeSettingsService {
	private readonly api = inject(NodeSettingsApiService);

	private nodeSettings$$ = new BehaviorSubject<NodeSettingItem[]>([]);
	public nodeSettings$ = this.nodeSettings$$.asObservable();
	private readonly loadingCount$$ = new BehaviorSubject(0);
	private readonly loading$$ = new BehaviorSubject(false);
	public readonly loading$ = merge(
		this.loadingCount$$.asObservable().pipe(map((count) => count > 0)),
		this.loading$$
	);

	public async load(force = false): Promise<void> {
		if (force || this.loadingCount$$.value === 0) {
			try {
				this.loadingCount$$.next(this.loadingCount$$.value + 1);
				const settings = await this.api.loadSettings();
				this.nodeSettings$$.next(settings);
				// eslint-disable-next-line no-useless-catch
			} catch (error) {
				throw error;
			} finally {
				this.loadingCount$$.next(Math.max(0, this.loadingCount$$.value - 1));
			}
		}
	}

	public getAdditionalSettingValue(
		subtypeId: string,
		settingKey: string
	): AdditionalSettingsOptions | null {
		return (
			this.nodeSettings$$.value
				?.find((subtype) => subtype.subtypeId === subtypeId)
				?.additionalSettings?.find((setting) => setting.additionalSetting === settingKey)
				?.selectedOption ?? null
		);
	}

	public setNodeSubtypeSettings(subtypeId: string, value: Partial<NodeSettingItem>): void {
		this.nodeSettings$$.next(
			this.nodeSettings$$
				.getValue()
				.map((setting) =>
					setting.subtypeId === subtypeId ? { ...setting, ...value } : setting
				)
		);
	}

	public async setExtractedContentValue(
		subtypeId: string,
		key: string,
		selectedOption: string
	): Promise<void> {
		const settings = this.nodeSettings$$.getValue();
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const setting = settings.find((iSetting) => iSetting.subtypeId === subtypeId)!;
		const extractedContent = setting.extractedContent.map((content) =>
			content.content === key ? { ...content, selectedOption } : content
		);
		try {
			this.loading$$.next(true);
			await this.api.updateSettings(setting.id, { extractedContent });
			setting.extractedContent = extractedContent;
			this.nodeSettings$$.next([...settings]);
		} catch (e) {
			console.error('Could not update node settings extracted content: ', e);
		} finally {
			this.loading$$.next(false);
		}
	}

	public async setAdditionalSettingValue(
		subtypeId: string,
		key: string,
		selectedOption: AdditionalSettingsOptions
	): Promise<void> {
		const settings = this.nodeSettings$$.getValue();
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const setting = settings.find((iSetting) => iSetting.subtypeId === subtypeId)!;
		const additionalSettings = setting.additionalSettings.map((iSetting) =>
			iSetting.additionalSetting === key ? { ...iSetting, selectedOption } : iSetting
		);
		try {
			this.loading$$.next(true);
			await this.api.updateSettings(setting.id, { additionalSettings });
			setting.additionalSettings = additionalSettings;
			this.nodeSettings$$.next([...settings]);
		} catch (e) {
			console.error('Could not update node settings additional settings: ', e);
		} finally {
			this.loading$$.next(false);
		}
	}

	public async addSuggestedProperty(
		subtypeId: string,
		propertyId: CustomPropertyInterface['id']
	): Promise<void> {
		const settings = this.nodeSettings$$.getValue();
		const setting = settings.find((iSetting) => iSetting.subtypeId === subtypeId);
		try {
			if (setting) {
				this.loading$$.next(true);
				setting.suggestedPropertyIds = [
					...new Set(setting.suggestedPropertyIds.concat(propertyId)),
				];
				await this.api.updateSettings(setting.id, {
					suggestedPropertyIds: setting.suggestedPropertyIds,
				});

				this.nodeSettings$$.next([...settings]);
			}
		} catch (e) {
			console.error('Could not update node settings property: ', e);
		} finally {
			this.loading$$.next(false);
		}
	}

	public async removeSuggestedProperty(subtypeId: string, propId: string): Promise<void> {
		const settings = this.nodeSettings$$.getValue();
		const setting = settings.find((iSetting) => iSetting.subtypeId === subtypeId);
		try {
			if (setting) {
				this.loading$$.next(true);
				setting.suggestedPropertyIds = setting.suggestedPropertyIds.filter(
					(id) => id !== propId
				);
				await this.api.updateSettings(setting.id, setting);
				this.nodeSettings$$.next([...settings]);
			}
		} catch (e) {
			console.error('Could not update node settings property: ', e);
		} finally {
			this.loading$$.next(false);
		}
	}
}
